import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setTenantHeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TenantContext = createContext(null);
const STORAGE_KEY = "active_tenant";

/**
 * Which workspace (publisher) the current user is working in.
 *
 * Admins, staff and writers are pinned to their own — the backend ignores the
 * header for them anyway. The owner can switch; the choice survives a refresh,
 * and with nothing saved they land in their first workspace.
 */
export function TenantProvider({ children }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantIdState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setTenantId = useCallback((id) => {
    setTenantIdState(id);
    setTenantHeader(id);
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private browsing */
    }
  }, []);

  const refreshTenants = useCallback(async () => {
    if (!isOwner) return [];
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
      return data;
    } catch {
      return [];
    }
  }, [isOwner]);

  useEffect(() => {
    if (user === null) return undefined;
    let cancelled = false;
    const resolve = async () => {
      if (user === false) {
        setTenantId(null);
        return;
      }
      if (!isOwner) {
        setTenantId(user.tenant_id || null);
        return;
      }
      const list = await refreshTenants();
      if (cancelled) return;
      let saved = null;
      try {
        saved = window.localStorage.getItem(STORAGE_KEY);
      } catch {
        /* private browsing */
      }
      const pick = list.find((t) => t.tenant_id === saved) || list[0];
      setTenantId(pick ? pick.tenant_id : null);
    };
    setLoading(true);
    resolve()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, isOwner, refreshTenants, setTenantId]);

  const activeTenant =
    tenants.find((t) => t.tenant_id === tenantId) ||
    (user && !isOwner ? { tenant_id: user.tenant_id, name: user.tenant_name } : null);

  return (
    <TenantContext.Provider value={{ isOwner, tenants, tenantId, activeTenant, setTenantId, refreshTenants, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);
